import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Droplets, TrendingUp, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const WaterMonitoring = ({ user }) => {
  const [parameters, setParameters] = useState([]);
  const [pools, setPools] = useState([]);
  const [selectedPool, setSelectedPool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingParam, setEditingParam] = useState(null);
  const [formData, setFormData] = useState({
    poolId: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    pH: 7.4,
    chlorine: 1.5,
    combinedChlorine: 0.2,
    temperature: 28
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paramsRes, equipmentRes] = await Promise.all([
        axiosInstance.get('/water-params'),
        axiosInstance.get('/equipment')
      ]);
      setParameters(paramsRes.data);
      // Filtra SOLO le 4 piscine esatte
      const allowedPools = [
        'Piscina 15 metri',
        'Piscina 20 metri',
        'Piscina 25 metri',
        'Piscina 50 metri'
      ];
      const poolEquipment = equipmentRes.data.filter(e => 
        allowedPools.includes(e.name)
      );
      setPools(poolEquipment);
      if (poolEquipment.length > 0 && !selectedPool) {
        setSelectedPool(poolEquipment[0].id);
      }
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingParam) {
        await axiosInstance.put(`/water-params/${editingParam.id}`, formData);
        toast.success('Parametri aggiornati!');
      } else {
        await axiosInstance.post('/water-params', formData);
        toast.success('Parametri registrati!');
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleEdit = (param) => {
    setEditingParam(param);
    setFormData({
      poolId: param.poolId,
      date: param.date,
      time: param.time,
      pH: param.pH,
      chlorine: param.chlorine,
      combinedChlorine: param.combinedChlorine,
      temperature: param.temperature
    });
    setDialogOpen(true);
  };

  const handleDelete = async (paramId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo parametro?')) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/water-params/${paramId}`);
      toast.success('Parametro eliminato!');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const resetForm = () => {
    setFormData({
      poolId: '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      pH: 7.4,
      chlorine: 1.5,
      combinedChlorine: 0.2,
      temperature: 28
    });
    setEditingParam(null);
  };

  const canEdit = user?.role === 'admin' || user?.role === 'maintainer';
  const canAdd = user?.role === 'admin' || user?.role === 'maintainer' || user?.role === 'water_monitor';

  // State for text inputs (to allow comma typing)
  const [textInputs, setTextInputs] = useState({
    pH: '7.4',
    temperature: '28',
    chlorine: '1.5',
    combinedChlorine: '0.2'
  });

  // Helper function to handle decimal input with comma support
  const handleDecimalInput = (value, field) => {
    // Allow typing numbers, dots, and commas
    const cleanValue = value.replace(/[^0-9.,\-]/g, '');
    setTextInputs({ ...textInputs, [field]: cleanValue });
    
    // Parse and update formData
    const normalizedValue = cleanValue.replace(',', '.');
    const numValue = parseFloat(normalizedValue);
    if (!isNaN(numValue)) {
      setFormData({ ...formData, [field]: numValue });
    }
  };

  // Sync textInputs when formData changes (e.g., on edit)
  useEffect(() => {
    setTextInputs({
      pH: formData.pH.toString(),
      temperature: formData.temperature.toString(),
      chlorine: formData.chlorine.toString(),
      combinedChlorine: formData.combinedChlorine.toString()
    });
  }, [formData.pH, formData.temperature, formData.chlorine, formData.combinedChlorine]);

  const getStatusColor = (value, min, max) => {
    if (value < min || value > max) return 'text-red-600';
    return 'text-green-600';
  };

  const filteredParams = selectedPool
    ? parameters
        .filter(p => p.poolId === selectedPool)
        .sort((a, b) => {
          // Ordina per data e ora - più recenti prima
          const dateTimeA = new Date(`${a.date}T${a.time}`);
          const dateTimeB = new Date(`${b.date}T${b.time}`);
          return dateTimeB - dateTimeA; // Decrescente (più recenti prima)
        })
    : parameters;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="water-monitoring-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Monitoraggio Acqua</h1>
          <p className="text-gray-600">Parametri delle piscine</p>
        </div>
        {canAdd && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={resetForm}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                data-testid="add-water-param-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuova Rilevazione
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="water-param-dialog">
              <DialogHeader>
                <DialogTitle>{editingParam ? 'Modifica Rilevazione' : 'Nuova Rilevazione'}</DialogTitle>
                <DialogDescription>
                  {editingParam ? 'Aggiorna i parametri dell\'acqua' : 'Registra i parametri dell\'acqua'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="poolId">Piscina</Label>
                  <select
                    id="poolId"
                    value={formData.poolId}
                    onChange={(e) => setFormData({ ...formData, poolId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    data-testid="water-pool-select"
                  >
                    <option value="">Seleziona piscina</option>
                    {pools.map((pool) => (
                      <option key={pool.id} value={pool.id}>{pool.name}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date">Data</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      data-testid="water-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="time">Ora</Label>
                    <Input
                      id="time"
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      required
                      data-testid="water-time-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pH">pH</Label>
                    <Input
                      id="pH"
                      type="text"
                      inputMode="decimal"
                      value={textInputs.pH}
                      onChange={(e) => handleDecimalInput(e.target.value, 'pH')}
                      required
                      data-testid="water-ph-input"
                      placeholder="7.4"
                    />
                    <p className="text-xs text-gray-500 mt-1">Range: 6.5 - 7.6</p>
                  </div>
                  <div>
                    <Label htmlFor="temperature">Temperatura (°C)</Label>
                    <Input
                      id="temperature"
                      type="text"
                      inputMode="decimal"
                      value={textInputs.temperature}
                      onChange={(e) => handleDecimalInput(e.target.value, 'temperature')}
                      required
                      data-testid="water-temperature-input"
                      placeholder="28.5"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="chlorine">Cloro Libero (mg/L)</Label>
                    <Input
                      id="chlorine"
                      type="text"
                      inputMode="decimal"
                      value={textInputs.chlorine}
                      onChange={(e) => handleDecimalInput(e.target.value, 'chlorine')}
                      required
                      data-testid="water-chlorine-input"
                      placeholder="1.5"
                    />
                    <p className="text-xs text-gray-500 mt-1">Range: 0.7 - 1.5</p>
                  </div>
                  <div>
                    <Label htmlFor="combinedChlorine">Cloro Combinato (mg/L)</Label>
                    <Input
                      id="combinedChlorine"
                      type="text"
                      inputMode="decimal"
                      value={textInputs.combinedChlorine}
                      onChange={(e) => handleDecimalInput(e.target.value, 'combinedChlorine')}
                      required
                      data-testid="water-combined-chlorine-input"
                      placeholder="0.2"
                    />
                    <p className="text-xs text-gray-500 mt-1">Max: 0.4</p>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                  data-testid="water-submit-button"
                >
                  {editingParam ? 'Aggiorna' : 'Registra'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Pool Tabs */}
      {pools.length > 0 && (
        <Tabs value={selectedPool} onValueChange={setSelectedPool} className="w-full">
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${pools.length}, minmax(0, 1fr))` }}>
            {pools.map((pool) => (
              <TabsTrigger key={pool.id} value={pool.id} data-testid={`pool-tab-${pool.id}`}>
                {pool.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {pools.map((pool) => (
            <TabsContent key={pool.id} value={pool.id} className="space-y-6">
              {/* Latest Parameters Card */}
              {filteredParams.length > 0 && (
                <Card className="shadow-lg border-0">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Droplets className="w-5 h-5 mr-2 text-blue-600" />
                      Ultimi Parametri
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">pH</p>
                        <p className={`text-3xl font-bold ${getStatusColor(filteredParams[0].pH, 6.5, 7.6)}`}>
                          {filteredParams[0].pH}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">6.5 - 7.6</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Cloro Libero (mg/L)</p>
                        <p className={`text-3xl font-bold ${getStatusColor(filteredParams[0].chlorine, 0.7, 1.5)}`}>
                          {filteredParams[0].chlorine}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">0.7 - 1.5</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Cloro Combinato (mg/L)</p>
                        <p className={`text-3xl font-bold ${filteredParams[0].combinedChlorine > 0.4 ? 'text-red-600' : 'text-green-600'}`}>
                          {filteredParams[0].combinedChlorine}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Max 0.4</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Temperatura (°C)</p>
                        <p className="text-3xl font-bold text-blue-600">
                          {filteredParams[0].temperature}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mt-4">
                      Rilevato il {new Date(filteredParams[0].date).toLocaleDateString('it-IT')} alle {filteredParams[0].time} da {filteredParams[0].recordedBy}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* History */}
              <Card className="shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
                    Storico
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {filteredParams.length === 0 ? (
                    <p className="text-gray-500 text-center py-8" data-testid="no-water-params">Nessuna rilevazione disponibile</p>
                  ) : (
                    <div className="space-y-3" data-testid="water-params-list">
                      {filteredParams.slice(0, 10).map((param) => (
                        <div key={param.id} className="p-4 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors" data-testid={`water-param-${param.id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">
                                {new Date(param.date).toLocaleDateString('it-IT')} - {param.time}
                              </p>
                              <p className="text-sm text-gray-600">{param.recordedBy}</p>
                            </div>
                            {canEdit && (
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEdit(param)}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDelete(param.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">pH: </span>
                              <span className={`font-medium ${getStatusColor(param.pH, 6.5, 7.6)}`}>
                                {param.pH}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Cloro L: </span>
                              <span className={`font-medium ${getStatusColor(param.chlorine, 0.7, 1.5)}`}>
                                {param.chlorine}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Cloro C: </span>
                              <span className={`font-medium ${param.combinedChlorine > 0.4 ? 'text-red-600' : 'text-green-600'}`}>
                                {param.combinedChlorine}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Temp: </span>
                              <span className="font-medium text-blue-600">{param.temperature}°C</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
};

export default WaterMonitoring;